import {
  Security as SecurityIcon,
  Group as GroupIcon,
  Apps as ModuleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { 
  modulePermissionsApi, 
  ModulePermission, 
  CreateModulePermissionRequest,
  UpdateModulePermissionRequest,
  AVAILABLE_MODULES 
} from '../../services/modulePermissionsApi';
import { userApi, UserGroup as ApiUserGroup } from '../../services/userApi';

// Local UserGroup interface for component state
interface LocalUserGroup {
  id: string;
  name: string;
  description?: string;
}

const ModulePermissions: React.FC = () => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<ModulePermission | null>(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState<LocalUserGroup | null>(null);
  const [selectedModuleName, setSelectedModuleName] = useState<string>('');

  const queryClient = useQueryClient();

  // Kullanıcı gruplarını getir
  const { data: userGroups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['userGroups'],
    queryFn: userApi.getUserGroups
  });

  // Modül yetkilerini getir
  const { data: modulePermissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['modulePermissions'],
    queryFn: modulePermissionsApi.getModulePermissions
  });

  // Permission güncelleme
  const updatePermissionMutation = useMutation({
    mutationFn: async (permission: ModulePermission) => {
      if (!permission.id) throw new Error('Permission ID is required');
      
      const updateRequest: UpdateModulePermissionRequest = {
        canRead: permission.canRead,
        canCreate: permission.canCreate,
        canUpdate: permission.canUpdate,
        canDelete: permission.canDelete
      };
      
      await modulePermissionsApi.updateModulePermission(permission.id, updateRequest);
    },
    onSuccess: () => {
      toast.success('Modül yetkileri güncellendi');
      queryClient.invalidateQueries({ queryKey: ['modulePermissions'] });
    },
    onError: (error: any) => {
      toast.error('Hata: ' + (error.response?.data?.message || error.message));
    }
  });

  // Yeni permission ekleme
  const createPermissionMutation = useMutation({
    mutationFn: async (request: CreateModulePermissionRequest) => {
      await modulePermissionsApi.createModulePermission(request);
    },
    onSuccess: () => {
      toast.success('Yeni modül yetki eklendi');
      queryClient.invalidateQueries({ queryKey: ['modulePermissions'] });
    },
    onError: (error: any) => {
      toast.error('Hata: ' + (error.response?.data?.message || error.message));
    }
  });

  // Permission silme
  const deletePermissionMutation = useMutation({
    mutationFn: async (id: number) => {
      await modulePermissionsApi.deleteModulePermission(id);
    },
    onSuccess: () => {
      toast.success('Modül yetki silindi');
      queryClient.invalidateQueries({ queryKey: ['modulePermissions'] });
    },
    onError: (error: any) => {
      toast.error('Hata: ' + (error.response?.data?.message || error.message));
    }
  });

  const handleEditPermission = (permission: ModulePermission) => {
    setSelectedModule(permission);
    const userGroup = userGroups.find(g => g.id === permission.userGroupId);
    if (userGroup) {
      setSelectedUserGroup({
        id: userGroup.id,
        name: userGroup.name,
        description: userGroup.description
      });
    }
    setEditDialogOpen(true);
  };

  const handleSavePermission = () => {
    if (selectedModule && selectedUserGroup) {
      if (selectedModule.id && selectedModule.id > 0) {
        // Update existing permission
        updatePermissionMutation.mutate(selectedModule);
      } else {
        // Create new permission
        const createRequest: CreateModulePermissionRequest = {
          moduleName: selectedModule.moduleName,
          userGroupId: selectedUserGroup.id,
          canRead: selectedModule.canRead,
          canCreate: selectedModule.canCreate,
          canUpdate: selectedModule.canUpdate,
          canDelete: selectedModule.canDelete
        };
        createPermissionMutation.mutate(createRequest);
      }
      setEditDialogOpen(false);
    }
  };

  const handlePermissionChange = (field: keyof ModulePermission, value: boolean) => {
    if (selectedModule) {
      setSelectedModule({
        ...selectedModule,
        [field]: value
      });
    }
  };

  if (groupsLoading || permissionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Modül yetkileri yüklüyor...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Modül Yetkileri
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Kullanıcı gruplarının modüllere erişim yetkilerini yönetin.
      </Typography>

      {/* İstatistikler */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ModuleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4">{AVAILABLE_MODULES.length}</Typography>
                  <Typography color="text.secondary">Toplam Modül</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4">{userGroups.length}</Typography>
                  <Typography color="text.secondary">Kullanıcı Grubu</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4">
                    {modulePermissions.filter(p => p.canCreate || p.canUpdate || p.canDelete).length}
                  </Typography>
                  <Typography color="text.secondary">Aktif Yetki</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Yeni Yetki Ekle Butonu */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => {
            setSelectedModule(null);
            setSelectedUserGroup(null);
            setEditDialogOpen(true);
          }}
        >
          Yeni Modül Yetki Ekle
        </Button>
      </Box>

      {/* Permissions Tablosu */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Modül</TableCell>
                <TableCell>Kullanıcı Grubu</TableCell>
                <TableCell align="center">Okuma</TableCell>
                <TableCell align="center">Oluşturma</TableCell>
                <TableCell align="center">Güncelleme</TableCell>
                <TableCell align="center">Silme</TableCell>
                <TableCell align="center">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modulePermissions.map((permission) => {
                const userGroup = userGroups.find(g => g.id === permission.userGroupId);
                return (
                  <TableRow key={permission.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {permission.moduleName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userGroup?.name || 'Bilinmeyen Grup'} 
                        color="primary" 
                        variant="outlined" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canRead ? 'Evet' : 'Hayır'} 
                        color={permission.canRead ? 'success' : 'error'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canCreate ? 'Evet' : 'Hayır'} 
                        color={permission.canCreate ? 'success' : 'error'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canUpdate ? 'Evet' : 'Hayır'} 
                        color={permission.canUpdate ? 'success' : 'error'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={permission.canDelete ? 'Evet' : 'Hayır'} 
                        color={permission.canDelete ? 'success' : 'error'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditPermission(permission)}
                        >
                          Düzenle
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            if (permission.id && window.confirm('Bu yetki silinecek. Emin misiniz?')) {
                              deletePermissionMutation.mutate(permission.id);
                            }
                          }}
                        >
                          Sil
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit/Create Permission Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedModule ? 'Modül Yetki Düzenleme' : 'Yeni Modül Yetki Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!selectedModule && (
              <>
                <Autocomplete
                  value={selectedUserGroup}
                  onChange={(_, newValue) => setSelectedUserGroup(newValue)}
                  options={userGroups}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Kullanıcı Grubu"
                      placeholder="Bir grup seçin..."
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                
                <Autocomplete
                  value={selectedModule ? (selectedModule as any).moduleName : null}
                  onChange={(_, newValue: string | null) => {
                    if (newValue && selectedUserGroup) {
                      setSelectedModule({
                        id: 0,
                        moduleName: newValue,
                        userGroupId: selectedUserGroup.id,
                        canRead: true,
                        canCreate: false,
                        canUpdate: false,
                        canDelete: false
                      });
                    }
                  }}
                  options={AVAILABLE_MODULES as readonly string[]}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Modül"
                      placeholder="Bir modül seçin..."
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                
                <Divider sx={{ my: 2 }} />
              </>
            )}
            
            {selectedModule && (
              <>
                {selectedUserGroup && (
                  <Typography variant="h6" gutterBottom>
                    {selectedModule.moduleName} - {selectedUserGroup.name}
                  </Typography>
                )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedModule.canRead}
                      onChange={(e) => handlePermissionChange('canRead', e.target.checked)}
                    />
                  }
                  label="Okuma Yetkisi"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedModule.canCreate}
                      onChange={(e) => handlePermissionChange('canCreate', e.target.checked)}
                    />
                  }
                  label="Oluşturma Yetkisi"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedModule.canUpdate}
                      onChange={(e) => handlePermissionChange('canUpdate', e.target.checked)}
                    />
                  }
                  label="Güncelleme Yetkisi"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedModule.canDelete}
                      onChange={(e) => handlePermissionChange('canDelete', e.target.checked)}
                    />
                  }
                  label="Silme Yetkisi"
                />
              </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CancelIcon />}>
            İptal
          </Button>
          <Button 
            onClick={handleSavePermission} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={updatePermissionMutation.isPending}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModulePermissions;
